import React, { useMemo, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { IconPlus, IconPencil, IconTrash, IconUsers } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useLoading } from '../context/LoadingContext';
import { staffForClient } from '../utils/clients';
import ConfirmModal from '../components/ConfirmModal';

const WRITES_TO_SHEET = Boolean(process.env.REACT_APP_APPS_SCRIPT_URL);

const EMPTY_CLIENT_FORM = {
  name: '',
  description: '',
  industry: '',
  contactName: '',
  contactTitle: '',
  contactEmail: '',
  contactPhone: '',
};

// Same inline re-auth pattern as Holidays/Announcements — a quick Google
// sign-in right in the modal footer when the token's gone stale.
function ReauthFooter({ setIdToken, onCancel }) {
  const { showError } = useToast();
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-muted text-center">Your session needs a quick refresh before this can be saved.</p>
      <GoogleLogin
        onSuccess={(credentialResponse) => setIdToken(credentialResponse.credential)}
        onError={() => showError('Google sign-in failed — please try again.')}
      />
      <button onClick={onCancel} className="text-sm font-medium text-muted hover:text-ink transition-colors">
        Cancel
      </button>
    </div>
  );
}

export default function Clients() {
  const {
    users,
    clients,
    clientStaff,
    createClient,
    updateClient,
    deleteClient,
    addClientStaff,
    removeClientStaff,
    setIdToken,
    isIdTokenValid,
  } = useApp();
  const { showSuccess, showError } = useToast();
  const { withLoading } = useLoading();
  const needsReauth = WRITES_TO_SHEET && !isIdTokenValid;

  const sortedClients = useMemo(() => [...clients].sort((a, b) => a.name.localeCompare(b.name)), [clients]);

  // --- Client create/edit/delete ---
  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT_FORM);
  const [deleteClientTarget, setDeleteClientTarget] = useState(null);

  const openCreateClient = () => {
    setEditingClientId(null);
    setClientForm(EMPTY_CLIENT_FORM);
    setClientFormOpen(true);
  };

  const openEditClient = (c) => {
    setEditingClientId(c.client_id);
    setClientForm({
      name: c.name,
      description: c.description || '',
      industry: c.industry || '',
      contactName: c.contact_name || '',
      contactTitle: c.contact_title || '',
      contactEmail: c.contact_email || '',
      contactPhone: c.contact_phone || '',
    });
    setClientFormOpen(true);
  };

  const closeClientForm = () => setClientFormOpen(false);

  const confirmSaveClient = async () => {
    if (!clientForm.name.trim()) {
      showError('Give the client a name.');
      return;
    }
    const name = clientForm.name.trim();
    const description = clientForm.description.trim();
    const industry = clientForm.industry.trim();
    const contactName = clientForm.contactName.trim();
    const contactTitle = clientForm.contactTitle.trim();
    const contactEmail = clientForm.contactEmail.trim();
    const contactPhone = clientForm.contactPhone.trim();

    try {
      if (editingClientId) {
        await withLoading(() =>
          updateClient({
            clientId: editingClientId,
            name,
            description,
            industry,
            contactName,
            contactTitle,
            contactEmail,
            contactPhone,
          })
        );
        showSuccess('Client updated.');
      } else {
        await withLoading(() =>
          createClient({ name, description, industry, contactName, contactTitle, contactEmail, contactPhone })
        );
        showSuccess('Client added.');
      }
      closeClientForm();
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  const confirmDeleteClient = async () => {
    if (!deleteClientTarget) return;
    try {
      await withLoading(() => deleteClient({ clientId: deleteClientTarget }));
      showSuccess('Client deleted.');
      setDeleteClientTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't delete that — please try again.");
    }
  };

  // --- Staff management ---
  const [staffClientId, setStaffClientId] = useState(null); // null = modal closed
  const [staffSearch, setStaffSearch] = useState('');
  const [roleDrafts, setRoleDrafts] = useState({}); // uuid -> in-progress role text

  const openStaff = (clientId) => {
    setStaffClientId(clientId);
    setStaffSearch('');
    setRoleDrafts({});
  };

  const closeStaff = () => setStaffClientId(null);

  const assignedStaff = useMemo(
    () => (staffClientId ? staffForClient(staffClientId, clientStaff, users) : []),
    [staffClientId, clientStaff, users]
  );

  const availableUsers = useMemo(() => {
    if (!staffClientId) return [];
    const assignedIds = new Set(assignedStaff.map((s) => s.uuid));
    const query = staffSearch.trim().toLowerCase();
    return users
      .filter((u) => !assignedIds.has(u.uuid) && (!query || u.name.toLowerCase().includes(query)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [staffClientId, users, assignedStaff, staffSearch]);

  const confirmAddStaff = async (targetUuid) => {
    const role = (roleDrafts[targetUuid] || '').trim();
    try {
      await withLoading(() => addClientStaff({ clientId: staffClientId, targetUuid, role }));
      setRoleDrafts((d) => {
        const next = { ...d };
        delete next[targetUuid];
        return next;
      });
      showSuccess('Staff added.');
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  const confirmRemoveStaff = async (targetUuid) => {
    try {
      await withLoading(() => removeClientStaff({ clientId: staffClientId, targetUuid }));
      showSuccess('Staff removed.');
    } catch (err) {
      showError(err.message || "Couldn't remove that — please try again.");
    }
  };

  const staffClient = clients.find((c) => c.client_id === staffClientId);

  return (
    <>
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <p className="text-sm text-muted">Manage Tux's clients and who's handling each one.</p>
          <button
            onClick={openCreateClient}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors text-sm whitespace-nowrap flex-shrink-0"
          >
            <IconPlus size={16} stroke={2} />
            New
          </button>
        </div>

        <div className="mt-6 space-y-3">
          {sortedClients.length === 0 && <p className="text-sm text-muted text-center py-10">No clients yet.</p>}
          {sortedClients.map((c) => {
            const staff = staffForClient(c.client_id, clientStaff, users);
            return (
              <div key={c.client_id} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-ink truncate">{c.name}</p>
                      {c.industry && (
                        <span className="inline-flex items-center rounded-full font-medium px-2.5 py-0.5 text-[11px] tracking-wide bg-light-gold text-bronze">
                          {c.industry}
                        </span>
                      )}
                    </div>
                    {c.description && <p className="text-sm text-muted mt-1">{c.description}</p>}
                    <p className="text-xs text-muted mt-2">
                      {staff.length === 0 ? 'No one assigned yet' : staff.map((s) => s.name).join(', ')}
                    </p>
                    {c.contact_name && (
                      <p className="text-xs text-muted mt-0.5">
                        Contact: {c.contact_name}
                        {c.contact_title && ` — ${c.contact_title}`}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1">
                    <button
                      onClick={() => openStaff(c.client_id)}
                      aria-label="Manage staff"
                      title="Manage staff"
                      className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                    >
                      <IconUsers size={16} stroke={1.8} />
                    </button>
                    <button
                      onClick={() => openEditClient(c)}
                      aria-label="Edit"
                      className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                    >
                      <IconPencil size={16} stroke={1.8} />
                    </button>
                    <button
                      onClick={() => setDeleteClientTarget(c.client_id)}
                      aria-label="Delete"
                      className="p-1.5 rounded-lg text-muted hover:text-error-tx hover:bg-card-tint transition-colors"
                    >
                      <IconTrash size={16} stroke={1.8} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ConfirmModal
        open={clientFormOpen}
        title={editingClientId ? 'Edit client' : 'New client'}
        confirmLabel="Save"
        onConfirm={confirmSaveClient}
        onCancel={closeClientForm}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeClientForm} /> : undefined}
        size="lg"
      >
        <div className="max-h-[60vh] overflow-y-auto pr-3 scroll-thin space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Client name</label>
            <input
              type="text"
              value={clientForm.name}
              onChange={(e) => setClientForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Acme Corp"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Industry</label>
            <input
              type="text"
              value={clientForm.industry}
              onChange={(e) => setClientForm((f) => ({ ...f, industry: e.target.value }))}
              placeholder="e.g. Retail"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Description</label>
            <textarea
              value={clientForm.description}
              onChange={(e) => setClientForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="What does this client do?"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
            />
          </div>

          <div className="border-t border-border pt-3 space-y-3">
            <p className="text-xs font-medium text-muted uppercase tracking-wide">Contact person</p>
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Name</label>
              <input
                type="text"
                value={clientForm.contactName}
                onChange={(e) => setClientForm((f) => ({ ...f, contactName: e.target.value }))}
                placeholder="e.g. Jane Doe"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Title</label>
              <input
                type="text"
                value={clientForm.contactTitle}
                onChange={(e) => setClientForm((f) => ({ ...f, contactTitle: e.target.value }))}
                placeholder="e.g. Marketing Director"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Email</label>
              <input
                type="text"
                value={clientForm.contactEmail}
                onChange={(e) => setClientForm((f) => ({ ...f, contactEmail: e.target.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Phone</label>
              <input
                type="text"
                value={clientForm.contactPhone}
                onChange={(e) => setClientForm((f) => ({ ...f, contactPhone: e.target.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
          </div>
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(deleteClientTarget)}
        title="Delete this client?"
        confirmLabel="Delete"
        onConfirm={confirmDeleteClient}
        onCancel={() => setDeleteClientTarget(null)}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={() => setDeleteClientTarget(null)} /> : undefined}
      >
        <p>This also removes everyone's staff assignment for this client. This can't be undone.</p>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(staffClientId)}
        title={staffClient ? `Staff — ${staffClient.name}` : 'Staff'}
        footer={
          needsReauth ? (
            <ReauthFooter setIdToken={setIdToken} onCancel={closeStaff} />
          ) : (
            <button
              onClick={closeStaff}
              className="w-full py-2.5 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors"
            >
              Done
            </button>
          )
        }
        onConfirm={closeStaff}
        onCancel={closeStaff}
      >
        <div>
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Add staff</label>
          {users.length === 0 ? (
            <p className="text-sm text-muted">No team members found.</p>
          ) : (
            <>
              <input
                type="text"
                value={staffSearch}
                onChange={(e) => setStaffSearch(e.target.value)}
                placeholder="Search team members..."
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <div className="mt-2 max-h-40 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {availableUsers.length === 0 ? (
                  <p className="text-xs text-muted px-3 py-2">
                    {staffSearch.trim() ? 'No matches.' : 'Everyone is already assigned.'}
                  </p>
                ) : (
                  availableUsers.map((u) => (
                    <div key={u.uuid} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="flex-1 text-ink truncate">{u.name}</span>
                      <input
                        type="text"
                        value={roleDrafts[u.uuid] || ''}
                        onChange={(e) => setRoleDrafts((d) => ({ ...d, [u.uuid]: e.target.value }))}
                        placeholder="Role"
                        className="w-24 border border-border rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-gold"
                      />
                      <button
                        type="button"
                        onClick={() => confirmAddStaff(u.uuid)}
                        className="text-xs font-semibold text-bronze hover:text-ink transition-colors flex-shrink-0"
                      >
                        Add
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {assignedStaff.length > 0 && (
          <div className="mt-3">
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Assigned</label>
            <div className="border border-border rounded-lg divide-y divide-border">
              {assignedStaff.map((s) => (
                <div key={s.uuid} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className="flex-1 text-ink truncate">
                    {s.name}
                    {s.role && <span className="text-muted"> — {s.role}</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => confirmRemoveStaff(s.uuid)}
                    className="text-xs font-semibold text-error-tx hover:text-ink transition-colors flex-shrink-0"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </ConfirmModal>
    </>
  );
}
